import { useState } from 'react';
import { DollarSign, TrendingUp, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { JobPostingFormData } from '../types';

interface BudgetCalculatorProps {
  formData: JobPostingFormData;
  onChange: (budgetData: Partial<JobPostingFormData>) => void;
  errors: Record<string, string>;
}

const BudgetCalculator = ({ formData, onChange, errors }: BudgetCalculatorProps) => {
  const [showRecommendations, setShowRecommendations] = useState(false);


  const getRecommendedBudget = () => {
    // Simple recommendation based on experience level
    const baseRate = 100;
    const multipliers = {
      entry: 1,
      intermediate: 2,
      expert: 4
    };
    
    const multiplier = multipliers[formData.experience_level];
    return {
      min: baseRate * multiplier,
      max: baseRate * multiplier * 3
    };
  };

  const recommendedBudget = getRecommendedBudget();

  const handleBudgetChange = (field: string, value: string) => {
    const numValue = value ? Number(value) : undefined;
    onChange({ [field]: numValue });
  };

  const handlePaymentTypeChange = (paymentType: string) => {
    onChange({ payment_type: paymentType as 'fixed' | 'hourly' | 'milestone' });
  };

  const handleCurrencyChange = (currency: string) => {
    onChange({ budget_currency: currency as 'BRO_SCORE' | 'TOP1_TOKEN' | 'USD' });
  };

  return (
    <div className="space-y-4">
      {/* Payment Type */}
      <div>
        <Label className="text-tan font-medium">
          Payment Type
        </Label>
        <Select value={formData.payment_type} onValueChange={handlePaymentTypeChange}>
          <SelectTrigger className="mt-1 bg-navy border-tan-dark text-tan">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-navy border-tan-dark">
            <SelectItem value="fixed">Fixed Price</SelectItem>
            <SelectItem value="hourly">Hourly Rate</SelectItem>
            <SelectItem value="milestone">Milestone Based</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-tan-dark text-xs mt-1">
          {formData.payment_type === 'fixed' && 'One-time payment for the entire project'}
          {formData.payment_type === 'hourly' && 'Payment based on hours worked'}
          {formData.payment_type === 'milestone' && 'Payment released at specific milestones'}
        </p>
      </div>

      {/* Currency */}
      <div>
        <Label className="text-tan font-medium">
          Currency
        </Label>
        <Select value={formData.budget_currency} onValueChange={handleCurrencyChange}>
          <SelectTrigger className="mt-1 bg-navy border-tan-dark text-tan">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-navy border-tan-dark">
            <SelectItem value="BRO_SCORE">BroScore (BS)</SelectItem>
            <SelectItem value="TOP1_TOKEN">$TOP1 Token</SelectItem>
            <SelectItem value="USD">USD ($)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Budget Range */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="budget_min" className="text-tan font-medium">
            {formData.payment_type === 'hourly' ? 'Min Hourly Rate' : 'Minimum Budget'}
          </Label>
          <div className="relative mt-1">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tan-dark" size={16} />
            <Input
              id="budget_min"
              type="number"
              placeholder="0"
              value={formData.budget_min || ''}
              onChange={(e) => handleBudgetChange('budget_min', e.target.value)}
              className={`pl-10 bg-navy border-tan-dark text-tan ${
                errors.budget_min ? 'border-red-500' : ''
              }`}
            />
          </div>
          {errors.budget_min && (
            <p className="text-red-400 text-xs mt-1">{errors.budget_min}</p>
          )}
        </div>

        <div>
          <Label htmlFor="budget_max" className="text-tan font-medium">
            {formData.payment_type === 'hourly' ? 'Max Hourly Rate' : 'Maximum Budget'}
          </Label>
          <div className="relative mt-1">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tan-dark" size={16} />
            <Input
              id="budget_max"
              type="number"
              placeholder="0"
              value={formData.budget_max || ''}
              onChange={(e) => handleBudgetChange('budget_max', e.target.value)}
              className={`pl-10 bg-navy border-tan-dark text-tan ${
                errors.budget_max ? 'border-red-500' : ''
              }`}
            />
          </div>
          {errors.budget_max && (
            <p className="text-red-400 text-xs mt-1">{errors.budget_max}</p>
          )}
        </div>
      </div>

      {/* Budget Recommendations */}
      <div className="bg-navy-light rounded-lg p-3 border border-tan-dark">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-tan font-medium flex items-center gap-2">
            <TrendingUp size={16} />
            Budget Recommendations
          </h4>
          <button
            type="button"
            onClick={() => setShowRecommendations(!showRecommendations)}
            className="text-tan-dark hover:text-tan text-xs"
          >
            {showRecommendations ? 'Hide' : 'Show'} Details
          </button>
        </div>

        <div className="text-sm text-tan-dark">
          <p>
            Recommended for {formData.experience_level} level: {' '}
            <span className="text-tan font-medium">
              {recommendedBudget.min}-{recommendedBudget.max} {formData.budget_currency}
            </span>
          </p>
        </div>

        {showRecommendations && (
          <div className="mt-3 space-y-2 text-xs">
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-navy rounded p-2">
                <div className="text-tan-dark">Entry Level</div>
                <div className="text-tan font-medium">100-300 BS</div>
              </div>
              <div className="bg-navy rounded p-2">
                <div className="text-tan-dark">Intermediate</div>
                <div className="text-tan font-medium">200-600 BS</div>
              </div>
              <div className="bg-navy rounded p-2">
                <div className="text-tan-dark">Expert</div>
                <div className="text-tan font-medium">400-1200 BS</div>
              </div>
            </div>

            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => onChange({
                  budget_min: recommendedBudget.min,
                  budget_max: recommendedBudget.max
                })}
                className="flex-1 bg-tan text-navy hover:bg-tan-dark rounded px-3 py-1 text-xs font-medium"
              >
                Use Recommended
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Payment Type Info */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          {formData.payment_type === 'fixed' && (
            <>
              <strong>Fixed Price:</strong> Best for well-defined projects with clear deliverables. 
              Payment is released when work is completed.
            </>
          )}
          {formData.payment_type === 'hourly' && (
            <>
              <strong>Hourly Rate:</strong> Good for ongoing work or projects with undefined scope. 
              Payment is based on tracked hours.
            </>
          )}
          {formData.payment_type === 'milestone' && (
            <>
              <strong>Milestone Based:</strong> Ideal for large projects. Payment is released at 
              specific project milestones.
            </>
          )}
        </AlertDescription>
      </Alert>

      {/* Budget Summary */}
      {(formData.budget_min || formData.budget_max) && (
        <div className="bg-navy rounded-lg p-3 border border-tan-dark">
          <h4 className="text-tan font-medium mb-2">Budget Summary</h4>
          <div className="text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-tan-dark">
                {formData.payment_type === 'hourly' ? 'Hourly Range:' : 'Project Budget:'}
              </span>
              <span className="text-tan">
                {formData.budget_min && formData.budget_max
                  ? `${formData.budget_min}-${formData.budget_max} ${formData.budget_currency}`
                  : formData.budget_min
                  ? `${formData.budget_min}+ ${formData.budget_currency}`
                  : formData.budget_max
                  ? `Up to ${formData.budget_max} ${formData.budget_currency}`
                  : 'Not specified'
                }
              </span>
            </div>
            
            {formData.payment_type === 'hourly' && formData.budget_min && formData.budget_max && (
              <div className="flex justify-between">
                <span className="text-tan-dark">Est. for 40 hours:</span>
                <span className="text-tan">
                  {formData.budget_min * 40}-{formData.budget_max * 40} {formData.budget_currency}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetCalculator;